import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { StorageService } from '../services/storageService';
import { User, UserRole, Issue } from '../types';
import { 
  Plus, 
  Search, 
  Shield, 
  Trash2, 
  Loader2, 
  Mail, 
  User as UserIcon,
  CheckCircle2,
  XCircle,
  AlertOctagon,
  KeyRound,
  Lock,
  Eye,
  EyeOff,
  MapPin,
  Pencil,
  UserPlus
} from 'lucide-react';
import { useAuth } from '../components/AuthContext';
import { BRANCH_LOCATIONS } from '../constants';

const Users: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser, loading: authLoading } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Add User Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: UserRole.STAFF,
    assignedOutlets: [] as string[],
    viewOnlyAssigned: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Reset Password Modal State
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [resetTargetUser, setResetTargetUser] = useState<User | null>(null);
  const [newManualPassword, setNewManualPassword] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [resetError, setResetError] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);

  // Edit User Modal State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editTargetUser, setEditTargetUser] = useState<User | null>(null);
  const [editData, setEditData] = useState({
    name: '',
    role: UserRole.STAFF,
    assignedOutlets: [] as string[],
    viewOnlyAssigned: false,
    newPassword: '',
    confirmNewPassword: '',
  });
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [showEditConfirmPassword, setShowEditConfirmPassword] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      if (!currentUser) return;
      if (currentUser.role !== UserRole.ADMIN) {
        navigate('/dashboard');
        return;
      }
      
      const [allUsers, allIssues] = await Promise.all([
        StorageService.getUsers(),
        StorageService.getIssues({ summary: true })
      ]);
      setUsers(allUsers);
      setIssues(allIssues);
    } catch (e: any) {
      console.error(e);
      setError(e.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    if (currentUser) {
        if (currentUser.role !== UserRole.ADMIN) {
            navigate('/dashboard');
            return;
        }
        fetchData();
    }
  }, [fetchData, currentUser, navigate]);

  const loadUsers = async () => {
    try {
      const fetchedUsers = await StorageService.getUsers();
      setUsers(fetchedUsers);
    } catch (e: any) {
       setError(e.message);
    }
  };

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    if (formData.password !== formData.confirmPassword) {
      setSubmitError('Passwords do not match.');
      setIsSubmitting(false);
      return;
    }

    if ((formData.role === UserRole.MANAGER || formData.role === UserRole.STAFF) && formData.assignedOutlets.length === 0) {
      setSubmitError('Please select at least one outlet for this role.');
      setIsSubmitting(false);
      return;
    }

    try {
      await StorageService.createUserAsAdmin(
        formData.email,
        formData.password,
        formData.name,
        formData.role,
        formData.assignedOutlets,
        formData.viewOnlyAssigned
      );
      // Wait a moment for trigger to run
      await new Promise(resolve => setTimeout(resolve, 1000));
      await loadUsers();
      setIsModalOpen(false);
      setFormData({ name: '', email: '', password: '', confirmPassword: '', role: UserRole.STAFF, assignedOutlets: [], viewOnlyAssigned: false });
      setShowPassword(false);
      setShowConfirmPassword(false);
    } catch (err: any) {
      setSubmitError(err.message || 'Failed to create user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (user: User) => {
    setEditTargetUser(user);
    setEditData({
      name: user.name,
      role: user.role,
      assignedOutlets: user.assignedOutlets || [],
      viewOnlyAssigned: user.viewOnlyAssigned || false,
      newPassword: '',
      confirmNewPassword: '',
    });
    setEditError('');
    setShowEditPassword(false);
    setShowEditConfirmPassword(false);
    setIsEditModalOpen(true);
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editTargetUser) return;

    setEditError('');

    if (editData.newPassword && editData.newPassword !== editData.confirmNewPassword) {
      setEditError('Passwords do not match.');
      return;
    }
    if (editData.newPassword && editData.newPassword.length < 6) {
      setEditError('New password must be at least 6 characters.');
      return;
    }
    if ((editData.role === UserRole.MANAGER || editData.role === UserRole.STAFF) && editData.assignedOutlets.length === 0) {
      setEditError('Please select at least one outlet for this role.');
      return;
    }

    setIsEditSubmitting(true);
    try {
      // Update name
      await StorageService.updateProfile(editTargetUser.id, { name: editData.name });
      // Update role
      if (editData.role !== editTargetUser.role) {
        await StorageService.updateUserRole(editTargetUser.id, editData.role);
      }
      // Update assigned outlets
      await StorageService.updateUserOutlets(editTargetUser.id, (editData.role === UserRole.MANAGER || editData.role === UserRole.STAFF) ? editData.assignedOutlets : []);
      // Update view restriction
      if (editData.viewOnlyAssigned !== editTargetUser.viewOnlyAssigned) {
        await StorageService.updateUserViewRestriction(editTargetUser.id, editData.viewOnlyAssigned);
      }
      // Update password only if provided
      if (editData.newPassword) {
        await StorageService.adminUpdateUserPassword(editTargetUser.id, editData.newPassword);
      }
      await loadUsers();
      setIsEditModalOpen(false);
    } catch (err: any) {
      setEditError(err.message || 'Failed to update user');
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: UserRole) => {
    try {
      // Optimistic update
      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
      await StorageService.updateUserRole(userId, newRole);
    } catch (err) {
      console.error("Failed to update role", err);
      // Revert on fail
      await loadUsers();
    }
  };

  const openResetModal = (user: User) => {
    setResetTargetUser(user);
    setNewManualPassword('');
    setResetError('');
    setShowResetPassword(false);
    setIsResetModalOpen(true);
  };

  const handleManualPasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetTargetUser || !newManualPassword) return;

    if (newManualPassword.length < 6) {
        setResetError("Password must be at least 6 characters");
        return;
    }

    setIsResetting(true);
    setResetError('');

    try {
        await StorageService.adminUpdateUserPassword(resetTargetUser.id, newManualPassword);
        setIsResetModalOpen(false);
    } catch (err: any) {
        setResetError(err.message || "Failed to update password");
    } finally {
        setIsResetting(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm("Are you sure you want to remove this user from the system? This will immediately revoke their access.")) {
      try {
        await StorageService.deleteUser(userId);
        setUsers(users.filter(u => u.id !== userId));
      } catch (err: any) {
        console.error("Deletion error:", err);
        alert(err.message || "Failed to delete user. Please check if the user has any dependent records (like reported issues).");
      }
    }
  };

  const getRoleBadgeColor = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN: return 'bg-purple-100 text-purple-700 border-purple-200';
      case UserRole.TECHNICIAN: return 'bg-blue-100 text-blue-700 border-blue-200';
      case UserRole.MANAGER: return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  if (authLoading || (loading && !users.length)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
     return (
        <Layout user={currentUser || { id: '', name: 'Guest', role: UserRole.STAFF, email: '' }}>
            <div className="p-8 text-center">
                <AlertOctagon className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-slate-800 mb-2">Error Loading Users</h2>
                <p className="text-slate-600 bg-red-50 p-3 rounded-lg inline-block border border-red-100">{error}</p>
            </div>
        </Layout>
     );
  }

  if (!currentUser) return null;

  return (
    <Layout user={currentUser}>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">User Management</h1>
          <p className="text-sm text-slate-500">Manage team members and access levels</p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add User
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Toolbar */}
        <div className="p-4 border-b border-slate-100">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-slate-900"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
              <tr>
                <th className="px-6 py-3">User</th>
                <th className="px-6 py-3">Role</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-slate-500">
                    No users found.
                  </td>
                </tr>
              ) : (
                filteredUsers.map(user => (
                  <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <img 
                          src={user.avatar || `https://ui-avatars.com/api/?name=${user.name}`} 
                          alt={user.name} 
                          className="w-10 h-10 rounded-full border border-slate-200"
                        />
                        <div className="ml-3">
                          <div className="text-sm font-medium text-slate-900">{user.name}</div>
                          <div className="text-xs text-slate-500 flex items-center mt-0.5">
                            <Mail className="w-3 h-3 mr-1" />
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <select 
                        value={user.role}
                        onChange={(e) => handleRoleChange(user.id, e.target.value as UserRole)}
                        disabled={user.id === currentUser.id} // Prevent changing own role effectively to avoid lockout
                        className={`text-xs font-semibold px-2 py-1 rounded-full border appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 bg-white text-slate-900 ${getRoleBadgeColor(user.role)}`}
                      >
                         <option value={UserRole.STAFF}>STAFF</option>
                         <option value={UserRole.TECHNICIAN}>TECHNICIAN</option>
                         <option value={UserRole.MANAGER}>MANAGER</option>
                         <option value={UserRole.ADMIN}>ADMIN</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full border border-emerald-100">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Active
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                            onClick={() => openEditModal(user)}
                            className="text-slate-400 hover:text-indigo-600 transition-colors p-1"
                            title="Edit User"
                        >
                            <Pencil className="w-5 h-5" />
                        </button>
                        <button 
                            onClick={() => openResetModal(user)}
                            className="text-slate-400 hover:text-blue-600 transition-colors p-1"
                            title="Reset Password Manually"
                        >
                            <KeyRound className="w-5 h-5" />
                        </button>
                        <button 
                            onClick={() => handleDeleteUser(user.id)}
                            disabled={user.id === currentUser.id}
                            className={`p-1 transition-colors ${
                                user.id === currentUser.id 
                                    ? 'text-slate-200 cursor-not-allowed' 
                                    : issues.some(i => i.reportedBy === user.id)
                                        ? 'text-amber-400 hover:text-amber-600'
                                        : 'text-slate-400 hover:text-red-600'
                            }`}
                            title={
                                user.id === currentUser.id 
                                    ? "You cannot delete yourself" 
                                    : issues.some(i => i.reportedBy === user.id)
                                        ? "This user has reported issues and cannot be deleted directly. Reassign their issues first."
                                        : "Remove User"
                            }
                        >
                            <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-md:max-w-md flex flex-col max-h-[90vh]">
            {/* Fixed header */}
            <div className="flex justify-between items-center px-6 pt-6 pb-4 border-b border-slate-100 flex-shrink-0">
              <h2 className="text-xl font-bold text-slate-900">Add New User</h2>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            {/* Scrollable form body */}
            <form id="create-user-form" onSubmit={handleCreateUser} className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-slate-900"
                    placeholder="Jane Doe"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-slate-900"
                    placeholder="jane@company.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value as UserRole, assignedOutlets: []})}
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-slate-900"
                  >
                    <option value={UserRole.STAFF}>Outlet Staff</option>
                    <option value={UserRole.TECHNICIAN}>Technician</option>
                    <option value={UserRole.MANAGER}>Manager</option>
                    <option value={UserRole.ADMIN}>Administrator</option>
                  </select>
                </div>
              </div>

              {/* Outlet Assignment — visible for Manager and Staff roles */}
              {(formData.role === UserRole.MANAGER || formData.role === UserRole.STAFF) && (
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-1.5 text-sm font-semibold text-blue-800">
                      <MapPin className="w-4 h-4" />
                      Assign Outlets
                    </label>
                    <span className="text-xs text-blue-600 font-medium bg-blue-100 px-2 py-0.5 rounded-full">
                      {formData.assignedOutlets.length === 0
                        ? 'None selected'
                        : formData.assignedOutlets.includes('__ALL__')
                        ? 'All outlets'
                        : `${formData.assignedOutlets.length} selected`}
                    </span>
                  </div>
                  <p className="text-xs text-blue-600">
                    {formData.role === UserRole.MANAGER
                      ? 'Manager will only see issues from the selected outlets.'
                      : 'Staff will only be able to report issues for the selected outlets.'}
                  </p>
                  <div className="space-y-1 max-h-36 overflow-y-auto pr-1">
                    {/* All Outlets option */}
                    <label className="flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer hover:bg-white transition-colors border border-transparent hover:border-blue-200 font-semibold text-slate-700">
                      <input
                        type="checkbox"
                        checked={formData.assignedOutlets.includes('__ALL__')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({...formData, assignedOutlets: ['__ALL__']});
                          } else {
                            setFormData({...formData, assignedOutlets: []});
                          }
                        }}
                        className="w-4 h-4 rounded accent-blue-600"
                      />
                      <span className="text-sm">All Outlets</span>
                    </label>
                    <div className="border-t border-blue-200 my-1" />
                    {/* Individual outlets */}
                    {BRANCH_LOCATIONS.map(outlet => (
                      <label key={outlet} className="flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer hover:bg-white transition-colors border border-transparent hover:border-blue-200">
                        <input
                          type="checkbox"
                          disabled={formData.assignedOutlets.includes('__ALL__')}
                          checked={formData.assignedOutlets.includes('__ALL__') || formData.assignedOutlets.includes(outlet)}
                          onChange={(e) => {
                            const updated = e.target.checked
                              ? [...formData.assignedOutlets, outlet]
                              : formData.assignedOutlets.filter(o => o !== outlet);
                            setFormData({...formData, assignedOutlets: updated});
                          }}
                          className="w-4 h-4 rounded accent-blue-600"
                        />
                        <span className="text-sm text-slate-700">{outlet}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* View Only Assigned Only option for Manager */}
              {formData.role === UserRole.MANAGER && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.viewOnlyAssigned}
                      onChange={(e) => setFormData({...formData, viewOnlyAssigned: e.target.checked})}
                      className="w-4 h-4 rounded accent-blue-600"
                    />
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-slate-800">Allow "Assign to Me"</span>
                      <span className="text-xs text-slate-500">Enable this to allow this manager to assign issues to themselves.</span>
                    </div>
                  </label>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Temporary Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full pl-10 pr-10 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-slate-900"
                    placeholder="Create a password"
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Must be at least 6 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 bg-white text-slate-900 transition-colors
                      ${formData.confirmPassword && formData.password !== formData.confirmPassword
                        ? 'border-red-300 focus:ring-red-400'
                        : formData.confirmPassword && formData.password === formData.confirmPassword
                        ? 'border-emerald-400 focus:ring-emerald-400'
                        : 'border-slate-300 focus:ring-blue-500'}`}
                    placeholder="Re-enter password"
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                )}
                {formData.confirmPassword && formData.password === formData.confirmPassword && (
                  <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Passwords match
                  </p>
                )}
              </div>

              {submitError && (
                <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">
                  {submitError}
                </div>
              )}
            </form>

            {/* Fixed footer buttons */}
            <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="create-user-form"
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center"
              >
                {isSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Create User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Password Reset Modal */}
      {isResetModalOpen && resetTargetUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Reset Password</h2>
                <p className="text-sm text-slate-500">For {resetTargetUser.name}</p>
              </div>
              <button 
                onClick={() => setIsResetModalOpen(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleManualPasswordReset} className="space-y-4">
              <div className="bg-amber-50 p-3 rounded-lg flex items-start text-xs text-amber-800 mb-4">
                <Shield className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                This will immediately overwrite the user's current password.
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type={showResetPassword ? 'text' : 'password'}
                    required
                    value={newManualPassword}
                    onChange={(e) => setNewManualPassword(e.target.value)}
                    className="w-full pl-10 pr-10 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-slate-900"
                    placeholder="Enter new password"
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showResetPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {resetError && (
                <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">
                  {resetError}
                </div>
              )}

              <div className="pt-2 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsResetModalOpen(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isResetting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center"
                >
                  {isResetting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Update Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {isEditModalOpen && editTargetUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md flex flex-col max-h-[90vh]">
            {/* Fixed header */}
            <div className="flex justify-between items-center px-6 pt-6 pb-4 border-b border-slate-100 flex-shrink-0">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Edit User</h2>
                <p className="text-sm text-slate-500">{editTargetUser.email}</p>
              </div>
              <button onClick={() => setIsEditModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            {/* Scrollable form body */}
            <form id="edit-user-form" onSubmit={handleEditUser} className="overflow-y-auto flex-1 px-6 py-4 space-y-4">

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type="text"
                    required
                    value={editData.name}
                    onChange={(e) => setEditData({...editData, name: e.target.value})}
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-slate-900"
                    placeholder="Full name"
                  />
                </div>
              </div>

              {/* Email — read only */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type="email"
                    value={editTargetUser.email}
                    disabled
                    className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-400 cursor-not-allowed"
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1">Email cannot be changed</p>
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <select
                    value={editData.role}
                    disabled={editTargetUser.id === currentUser?.id}
                    onChange={(e) => setEditData({...editData, role: e.target.value as UserRole, assignedOutlets: []})}
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-slate-900 disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
                  >
                    <option value={UserRole.STAFF}>Outlet Staff</option>
                    <option value={UserRole.TECHNICIAN}>Technician</option>
                    <option value={UserRole.MANAGER}>Manager</option>
                    <option value={UserRole.ADMIN}>Administrator</option>
                  </select>
                </div>
                {editTargetUser.id === currentUser?.id && (
                  <p className="text-xs text-slate-400 mt-1">You cannot change your own role</p>
                )}
              </div>

              {/* Outlet Assignment — only for Manager and Staff */}
              {(editData.role === UserRole.MANAGER || editData.role === UserRole.STAFF) && (
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-1.5 text-sm font-semibold text-blue-800">
                      <MapPin className="w-4 h-4" />
                      Assign Outlets
                    </label>
                    <span className="text-xs text-blue-600 font-medium bg-blue-100 px-2 py-0.5 rounded-full">
                      {editData.assignedOutlets.length === 0
                        ? 'None selected'
                        : editData.assignedOutlets.includes('__ALL__')
                        ? 'All outlets'
                        : `${editData.assignedOutlets.length} selected`}
                    </span>
                  </div>
                  <p className="text-xs text-blue-600">
                    {editData.role === UserRole.MANAGER
                      ? 'Manager will only see issues from the selected outlets.'
                      : 'Staff will only be able to report issues for the selected outlets.'}
                  </p>
                  <div className="space-y-1 max-h-36 overflow-y-auto pr-1">
                    {/* All Outlets option */}
                    <label className="flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer hover:bg-white transition-colors border border-transparent hover:border-blue-200 font-semibold text-slate-700">
                      <input
                        type="checkbox"
                        checked={editData.assignedOutlets.includes('__ALL__')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEditData({...editData, assignedOutlets: ['__ALL__']});
                          } else {
                            setEditData({...editData, assignedOutlets: []});
                          }
                        }}
                        className="w-4 h-4 rounded accent-blue-600"
                      />
                      <span className="text-sm">All Outlets</span>
                    </label>
                    <div className="border-t border-blue-200 my-1" />
                    {/* Individual outlets */}
                    {BRANCH_LOCATIONS.map(outlet => (
                      <label key={outlet} className="flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer hover:bg-white transition-colors border border-transparent hover:border-blue-200">
                        <input
                          type="checkbox"
                          disabled={editData.assignedOutlets.includes('__ALL__')}
                          checked={editData.assignedOutlets.includes('__ALL__') || editData.assignedOutlets.includes(outlet)}
                          onChange={(e) => {
                            const updated = e.target.checked
                              ? [...editData.assignedOutlets, outlet]
                              : editData.assignedOutlets.filter(o => o !== outlet);
                            setEditData({...editData, assignedOutlets: updated});
                          }}
                          className="w-4 h-4 rounded accent-blue-600"
                        />
                        <span className="text-sm text-slate-700">{outlet}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* View Only Assigned Only option for Manager */}
              {editData.role === UserRole.MANAGER && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editData.viewOnlyAssigned}
                      onChange={(e) => setEditData({...editData, viewOnlyAssigned: e.target.checked})}
                      className="w-4 h-4 rounded accent-blue-600"
                    />
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-slate-800">Allow "Assign to Me"</span>
                      <span className="text-xs text-slate-500">Enable this to allow this manager to assign issues to themselves.</span>
                    </div>
                  </label>
                </div>
              )}

              {/* Reset Password Button in Edit Modal */}
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setResetTargetUser(editTargetUser);
                    setNewManualPassword('');
                    setResetError('');
                    setShowResetPassword(false);
                    setIsResetModalOpen(true);
                  }}
                  className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                >
                  <KeyRound className="w-4 h-4" />
                  Reset user password
                </button>
              </div>

              {editError && (
                <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">
                  {editError}
                </div>
              )}
            </form>

            {/* Fixed footer buttons */}
            <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="edit-user-form"
                disabled={isEditSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center"
              >
                {isEditSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Users;
